if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initOneSwipeOnePhoto();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      /* ---- Mobile: one swipe = one photo, with a smooth glide ----
         The gallery handles horizontal swipes itself instead of using the browser's momentum:
         the photo follows your finger, and on release it eases smoothly into the next photo
         (or back, for a short, slow drag). A hard flick never skips photos and never stops abruptly.
         Vertical page scrolling and two-finger pinch-zoom are left to the browser.
         Phones only (749px and below). */
      initOneSwipeOnePhoto() {
        const list = this.elements.viewer && this.elements.viewer.querySelector('.product__media-list');
        if (!list) return;
        const phone = window.matchMedia('(max-width: 749px)');
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        const DECIDE_DISTANCE = 6; // px moved before deciding horizontal vs vertical
        const SWIPE_DISTANCE = 40; // px dragged that always counts as a swipe
        const SWIPE_VELOCITY = 0.3; // px per ms; a quick flick counts even if short
        const EDGE_RESISTANCE = 0.35; // rubber-band feel past the first/last photo

        let startX = 0;
        let startY = 0;
        let startScroll = 0;
        let startIndex = 0;
        let direction = null; // 'h' | 'v' | null
        let active = false;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;
        let animationFrame = null;

        const applyTouchAction = () => {
          list.style.touchAction = phone.matches ? 'pan-y pinch-zoom' : '';
        };
        applyTouchAction();
        if (phone.addEventListener) phone.addEventListener('change', applyTouchAction);

        const getSlides = () =>
          Array.from(list.querySelectorAll('.slider__slide')).filter((slide) => slide.offsetWidth > 0);

        const getPadding = () => parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0;

        const maxScroll = () => list.scrollWidth - list.clientWidth;

        const slidePosition = (slide) =>
          Math.max(0, Math.min(maxScroll(), slide.offsetLeft - getPadding()));

        const getCurrentIndex = (slides) => {
          let closest = 0;
          let closestDistance = Infinity;
          slides.forEach((slide, index) => {
            const distance = Math.abs(slidePosition(slide) - list.scrollLeft);
            if (distance < closestDistance) {
              closestDistance = distance;
              closest = index;
            }
          });
          return closest;
        };

        const freeSnap = () => {
          list.style.scrollSnapType = 'none';
          list.style.scrollBehavior = 'auto';
        };

        const restoreSnap = () => {
          list.style.scrollSnapType = '';
          list.style.scrollBehavior = '';
        };

        const stopAnimation = () => {
          if (animationFrame) cancelAnimationFrame(animationFrame);
          animationFrame = null;
        };

        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

        const glideTo = (target) => {
          stopAnimation();
          const from = list.scrollLeft;
          const distance = target - from;
          if (Math.abs(distance) < 1 || reduceMotion.matches) {
            list.scrollLeft = target;
            restoreSnap();
            return;
          }
          // Longer distance = slightly longer glide, always between 260ms and 420ms
          const duration = Math.max(260, Math.min(420, Math.abs(distance) * 0.9));
          const startTime = performance.now();
          const step = (now) => {
            const progress = Math.min(1, (now - startTime) / duration);
            list.scrollLeft = from + distance * easeOutCubic(progress);
            if (progress < 1) {
              animationFrame = requestAnimationFrame(step);
            } else {
              animationFrame = null;
              restoreSnap();
            }
          };
          animationFrame = requestAnimationFrame(step);
        };

        const finishSwipe = (dx) => {
          const slides = getSlides();
          if (!slides.length) {
            restoreSnap();
            return;
          }
          let target = startIndex;
          if (Math.abs(dx) > SWIPE_DISTANCE || Math.abs(velocity) > SWIPE_VELOCITY) {
            const movingLeft = Math.abs(velocity) > SWIPE_VELOCITY ? velocity < 0 : dx < 0;
            target = startIndex + (movingLeft ? 1 : -1);
          }
          target = Math.max(0, Math.min(slides.length - 1, target));
          glideTo(slidePosition(slides[target]));
        };

        list.addEventListener(
          'touchstart',
          (event) => {
            if (!phone.matches || event.touches.length !== 1) {
              if (active && direction === 'h') finishSwipe(0);
              active = false;
              return;
            }
            stopAnimation();
            active = true;
            direction = null;
            startX = lastX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            lastTime = performance.now();
            velocity = 0;
            startScroll = list.scrollLeft;
            startIndex = getCurrentIndex(getSlides());
          },
          { passive: true }
        );

        list.addEventListener(
          'touchmove',
          (event) => {
            if (!active) return;
            if (event.touches.length > 1) {
              // Second finger down = pinch zoom; hand control back to the browser
              if (direction === 'h') finishSwipe(0);
              active = false;
              return;
            }
            const x = event.touches[0].clientX;
            const y = event.touches[0].clientY;
            const dx = x - startX;
            const dy = y - startY;

            if (!direction) {
              if (Math.abs(dx) < DECIDE_DISTANCE && Math.abs(dy) < DECIDE_DISTANCE) return;
              direction = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
              if (direction === 'v') {
                active = false;
                return;
              }
              freeSnap();
            }

            if (event.cancelable) event.preventDefault();

            let next = startScroll - dx;
            const max = maxScroll();
            if (next < 0) next = next * EDGE_RESISTANCE;
            if (next > max) next = max + (next - max) * EDGE_RESISTANCE;
            list.scrollLeft = next;

            const now = performance.now();
            const elapsed = now - lastTime;
            if (elapsed > 0) {
              // Smoothed finger speed, used to detect a quick flick
              velocity = 0.7 * ((x - lastX) / elapsed) + 0.3 * velocity;
            }
            lastX = x;
            lastTime = now;
          },
          { passive: false }
        );

        const onEnd = (event) => {
          if (!active) return;
          if (event.touches && event.touches.length > 0) return;
          active = false;
          if (direction !== 'h') return;
          // A finger resting still before release is not a flick
          if (performance.now() - lastTime > 100) velocity = 0;
          const touch = event.changedTouches && event.changedTouches[0];
          finishSwipe(touch ? touch.clientX - startX : 0);
        };

        list.addEventListener('touchend', onEnd, { passive: true });
        list.addEventListener('touchcancel', onEnd, { passive: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
